<script setup lang="ts">
import Dropdown from "../Dropdown/Dropdown.vue";
import { RaygunFunctions } from "@/config/RayGun";

const fav = useFavStore();
const profile = useProfileStore();
const route = useRoute();
const modals = useModalStore();
const props = defineProps<{
  showId?: string;
  songNumber: string;
  title: string | null;
  fitToContainer?: boolean;
  onBusyBg?: boolean;
}>();
const toast = useToastStore();
const favorite = ref(false);
const { t } = useI18n();
const id = computed(() => (route.params.id as string) || "");

async function onRemoveSongFromCurrentList() {
  await fav.removeSongFromList(id.value, props.songNumber);
  toast.successToast(
    t("SV._LIT_SongRemovedFromCurrentList", {
      title: props.title,
    }),
  );
  emitter.emit("songListUpdated", id.value);
  trackDropdownAction("remove from current list");
}
onMounted(() => checkFavStatus());
watch(
  () => props.songNumber,
  () => checkFavStatus(),
);

function checkFavStatus() {
  if (!props.songNumber) {
    return;
  }
  if (!profile.profile?.signedIn) {
    favorite.value = false;
    return;
  }
  favorite.value = false;
  if (fav.favSongNumbers?.includes) {
    favorite.value = fav.favSongNumbers?.includes(props.songNumber);
  } else if (fav.favSongNumbers && !fav.favSongNumbers.includes) {
    RaygunFunctions.sendSilentError("favSongNumbers doesn't have 'include'", {
      favSongNumbers: fav.favSongNumbers,
      typeofFavSongNumbers: typeof fav.favSongNumbers,
      songNumber: props.songNumber,
    });
  }
}

const onCurrentListView = computed(() => route.name === "favorites-list");
async function toggleFavorite() {
  if (!profile.profile?.signedIn) {
    modals.showModal("SubscriberFeatureModal");
  } else {
    favorite.value = !favorite.value;
    if (!favorite.value) {
      await fav.removeFromFavorites(props.songNumber);
      toast.successToast(
        t("SV.MES_RemovedFromFavoritesSuccess", {
          title: props.title,
        }),
      );
      trackDropdownAction("remove from personal favorites");
    } else {
      await fav.addToFavorites(props.songNumber);
      toast.successToast(
        t("SV.MES_AddedToFavoritesSuccess", { title: props.title }),
      );
      trackDropdownAction("add to personal favorites");
    }
    checkFavStatus();
  }
}

function addToSharedList(songNumber: string) {
  fav.addSongToSharedList(songNumber);
  trackDropdownAction("add to shared list");
}

function trackDropdownAction(action: string) {
  // GTM Tracking
  (window as any).dataLayer.push({
    event: "favoritesDropdownAction",
    dropdown_action_type: action,
    song_number: props.songNumber,
  });
  // Clear song_number
  (window as any).dataLayer.push({
    song_number: undefined,
  });
}

const dropdown = ref<InstanceType<typeof Dropdown>>();

watch(
  () => dropdown.value?.enableDOM,
  (value) => {
    props.showId && emitter.emit("alwaysShowId", props.showId);
  },
);
</script>

<template>
  <div
    v-if="songNumber && title"
    class="add-to-favorites"
    :class="{ 'fit-to-container': fitToContainer, 'on-busy-bg': onBusyBg }"
  >
    <Dropdown
      v-if="fav.enableSharedFavorites && profile.profile?.isAdmin"
      ref="dropdown"
      :class="{ 'favorite-active': favorite || onCurrentListView }"
      class="favorite dropdown-end"
      @click.stop.prevent
    >
      <template #btn-label>
        <FAIcon class="fa-solid fa-heart" />
        <span
          v-if="favorite"
          class="sr-only"
          >{{ $t("S.LIT_RemoveFromFavorites") }}</span
        ><span
          v-else
          class="sr-only"
          >{{ $t("S.LIT_AddToFavorites") }}</span
        >
      </template>

      <DropdownItem
        v-if="!favorite"
        icon-before="fa-light fa-plus"
        class="ga-favorites-dropdown"
        @click="toggleFavorite"
        >{{ $t("SV.LIT_AddToPersonalList") }}</DropdownItem
      >
      <DropdownItem
        v-else
        icon-before="fa-light fa-circle-minus"
        class="ga-favorites-dropdown"
        @click="toggleFavorite"
        >{{ $t("SV.LIT_RemoveFromPersonalList") }}</DropdownItem
      >

      <DropdownItem
        v-if="onCurrentListView"
        icon-before="fa-light fa-circle-minus"
        class="ga-favorites-dropdown"
        @click="onRemoveSongFromCurrentList"
        >{{ $t("SV.LIT_RemoveFromCurrentSharedList") }}</DropdownItem
      >
      <DropdownItem
        icon-before="fa-light fa-album-collection-circle-plus"
        class="ga-favorites-dropdown"
        @click="addToSharedList(songNumber)"
        >{{ $t("SV.LIT_AddToSharedList") }}</DropdownItem
      >
    </Dropdown>
    <div
      v-else
      class="favorite"
      :class="{ 'favorite-active': favorite }"
    >
      <button
        type="button"
        class="btn"
        @click.stop.prevent="toggleFavorite"
      >
        <FAIcon class="fa-solid fa-heart" />
        <span
          v-if="favorite"
          class="sr-only"
          >{{ $t("S.LIT_RemoveFromFavorites") }}</span
        ><span
          v-else
          class="sr-only"
          >{{ $t("S.LIT_AddToFavorites") }}</span
        >
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.favorite {
  @apply block;
}
.favorite:deep() {
  .btn {
    min-height: unset;
    height: 100%;
    @apply rounded border-none bg-transparent p-2 text-gray-300 shadow-none transition-all;
    &:hover {
      @apply bg-white text-gray-400;
    }
  }
}
.add-to-favorites.on-busy-bg:deep() {
  .btn {
    @apply h-8 w-8 rounded-full bg-white/25 text-white transition-all;
    &:hover,
    &:focus-visible {
      @apply brightness-90;
    }
  }
}

.favorite.favorite-active {
  &:deep() {
    .btn {
      i,
      svg {
        @apply text-red-500;
      }
    }
  }
}

.fit-to-container {
  @apply h-full w-full;
  :deep() {
    .dropdown {
      @apply h-full w-full;
      .btn {
        @apply flex h-full w-full items-center justify-center;
      }
    }
  }
}
</style>
